/**
 * Static Blog Service
 * 
 * Service for reading blog posts from markdown files.
 * This handles the static content portion of the hybrid approach.
 */

import {
  getAllPosts,
  getPostBySlug,
  getFeaturedPosts,
  getPostsByCategory,
  getPostsByTag,
} from '../utils/markdown';
import type { BlogPost, BlogQueryOptions } from '../types/blog.types';

/**
 * Filter and sort posts based on query options
 */
const filterAndSortPosts = (
  posts: BlogPost[],
  options: BlogQueryOptions
): BlogPost[] => {
  let filtered = [...posts];

  // Apply category filter
  if (options.category) {
    filtered = filtered.filter(
      (post) => post.categories && post.categories.includes(options.category!)
    );
  }

  // Apply tag filter
  if (options.tag) {
    filtered = filtered.filter(
      (post) => post.tags && post.tags.includes(options.tag!)
    );
  }

  // Apply featured filter
  if (options.featuredOnly) {
    filtered = filtered.filter((post) => post.isFeatured);
  }

  // Apply search filter
  if (options.search) {
    const searchLower = options.search.toLowerCase();
    filtered = filtered.filter(
      (post) =>
        post.title.toLowerCase().includes(searchLower) ||
        post.excerpt?.toLowerCase().includes(searchLower) ||
        post.content.toLowerCase().includes(searchLower)
    );
  }

  // Apply sorting
  if (options.sortBy) {
    filtered.sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (options.sortBy) {
        case 'publishedDate':
          aValue = new Date(a.publishedDate).getTime();
          bValue = new Date(b.publishedDate).getTime();
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'likes':
          aValue = a.metrics?.likes || 0;
          bValue = b.metrics?.likes || 0;
          break;
        case 'views':
          aValue = a.metrics?.views || 0;
          bValue = b.metrics?.views || 0;
          break;
        default:
          aValue = a.publishedDate;
          bValue = b.publishedDate;
      }

      if (options.sortOrder === 'desc') {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    });
  }

  // Apply pagination
  if (options.page && options.limit) {
    const start = (options.page - 1) * options.limit;
    const end = start + options.limit;
    filtered = filtered.slice(start, end);
  }

  return filtered;
};

/**
 * Static Blog Service API
 * 
 * Provides the same interface as the API-based service,
 * but reads from markdown files instead.
 */
export const staticBlogService = {
  /**
   * Get list of blog posts with filtering and pagination
   */
  getBlogList: async (options: BlogQueryOptions = {}): Promise<BlogPost[]> => {
    let posts: BlogPost[];

    // Optimize for common queries
    if (options.featuredOnly) {
      posts = await getFeaturedPosts();
    } else if (options.category) {
      posts = await getPostsByCategory(options.category);
    } else if (options.tag) {
      posts = await getPostsByTag(options.tag);
    } else {
      posts = await getAllPosts();
    }

    // Apply remaining filters and sorting
    return filterAndSortPosts(posts, options);
  },

  /**
   * Get a single blog post by slug
   */
  getBlogBySlug: async (slug: string): Promise<BlogPost | null> => {
    return await getPostBySlug(slug);
  },

  /**
   * Get a single blog post by ID (treats ID as slug)
   * For compatibility with existing code
   */
  getBlogById: async (id: string): Promise<BlogPost | null> => {
    return await getPostBySlug(id);
  },
};

